import { requestOtp } from '@/api/otp';
import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyContentPanel, SurveyContentPanelInset, SurveyQuestionPanel } from '@/components/layout/SurveyContentPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { TURNSTILE_CONTAINER_CLASS, useTurnstile } from '@/hooks/useTurnstile';
import { useTranslation } from '@/hooks/useTranslation';
import { isMaxSubmissionsReached } from '@/lib/maxSubmissions';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { setOtpDraft } from '@/stores/appSlice';
import { selectOtpDraft, selectPdpaConsent, selectStaffRef, selectSurveyScreening } from '@/stores/selectors';
import { displayFullMalaysiaMobile, formatLocalDigits, parseLocalMobileInput } from '@/utils/malaysiaMobile';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const lightInputClassName =
  'min-w-0 w-full rounded-sm border border-slate-300 bg-white px-3 py-2.5 text-[16px] font-semibold text-slate-900 placeholder:font-normal outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-300';

const mobileFieldShell =
  'flex min-w-0 w-full overflow-hidden rounded-sm border border-slate-300 bg-white focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-300';

const mobilePrefixClass =
  'flex shrink-0 items-center border-r border-slate-300 bg-slate-100 px-2.5 py-2.5 text-[16px] font-semibold tabular-nums text-slate-800';

const mobileLocalInputClass =
  'min-w-0 flex-1 border-0 bg-transparent px-3 py-2.5 text-[16px] font-semibold text-slate-900 outline-none tabular-nums';

export default function Phone() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { mobileLocalDigits, customerUuid, otpSentAtMs, otpVerified, submissionIndex, questionnaireSet } =
    useAppSelector(selectOtpDraft);
  const staffRef = useAppSelector(selectStaffRef);
  const [warning, setWarning] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifyingSecurity, setIsVerifyingSecurity] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const otpConfirmInFlightRef = useRef(false);
  const { remountTurnstile, executeTurnstile, turnstileRef, turnstileEnabled } = useTurnstile({
    enabled: showConfirmModal,
  });

  useEffect(() => {
    setWarning(null);
  }, [mobileLocalDigits]);

  useEffect(() => {
    if (!showConfirmModal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowConfirmModal(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showConfirmModal]);

  const patchDraft = (partial: { mobileLocalDigits?: string }) => {
    if (partial.mobileLocalDigits !== undefined) {
      dispatch(setOtpDraft({ mobileLocalDigits: partial.mobileLocalDigits }));
      return;
    }
    dispatch(
      setOtpDraft({
        mobileLocalDigits,
        customerUuid,
        ...(otpSentAtMs != null ? { otpSentAtMs } : {}),
        ...(otpVerified ? { otpVerified } : {}),
        ...(submissionIndex != null ? { submissionIndex } : {}),
        ...(questionnaireSet ? { questionnaireSet } : {}),
      })
    );
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!staffRef?.staffCode) {
    return <Navigate to="/staff-required" replace />;
  }

  const mobileDigits = mobileLocalDigits.replace(/\D/g, '');

  const openConfirm = () => {
    if (mobileDigits.length < 9) {
      setWarning(t.phone.mobileWarning);
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmRequestOtp = async () => {
    if (otpConfirmInFlightRef.current) {
      return;
    }
    otpConfirmInFlightRef.current = true;
    setIsSubmitting(true);
    let turnstileToken: string | null = null;
    let navigatedToOtp = false;
    try {
      if (turnstileEnabled) {
        setIsVerifyingSecurity(true);
        turnstileToken = await executeTurnstile();
        setIsVerifyingSecurity(false);
        if (!turnstileToken) {
          setWarning(t.phone.turnstileFailed);
          return;
        }
      }

      setWarning(null);
      const result = await requestOtp({
        mobileLocalDigits: mobileDigits,
        ageBand: surveyScreening.ageBand,
        language: surveyLanguage,
        staffCode: staffRef.staffCode,
        turnstileToken,
      });

      if (result.ok === false) {
        setShowConfirmModal(false);
        if (isMaxSubmissionsReached(result)) {
          navigate('/custcompleted', { replace: true });
          return;
        }
        setWarning(result.error);
        return;
      }

      const sentAt = Date.now();
      dispatch(
        setOtpDraft({
          mobileLocalDigits: mobileDigits,
          customerUuid: result.customerUuid,
          otpSentAtMs: sentAt,
        })
      );
      setShowConfirmModal(false);
      navigatedToOtp = true;
      navigate('/otp');
    } catch {
      setShowConfirmModal(false);
      setWarning(t.phone.otpError);
    } finally {
      setIsVerifyingSecurity(false);
      setIsSubmitting(false);
      otpConfirmInFlightRef.current = false;
      if (turnstileEnabled && !navigatedToOtp) {
        remountTurnstile();
      }
    }
  };

  return (
    <SurveyBackgroundLayout>
      {showConfirmModal ? (
        <div ref={turnstileRef} className={TURNSTILE_CONTAINER_CLASS} aria-hidden="true" />
      ) : null}
      <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="mx-auto mt-[18vh] flex w-full max-w-full flex-col py-6">
          <SurveyContentPanelInset>
            <SurveyQuestionPanel>
              <p className="m-0 w-full break-words text-justify text-wrap text-[16px] font-bold leading-snug text-white sm:text-[17px]">
                {t.phone.instruction}
              </p>
            </SurveyQuestionPanel>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-6">
            <SurveyContentPanel className="!p-4">
              <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-4">
                <label
                  htmlFor="mobile-local"
                  className="text-center text-[16px] font-bold text-white sm:text-[17px]"
                >
                  {t.phone.mobileLabel}
                </label>
                <div className={mobileFieldShell}>
                  <span className={mobilePrefixClass}>+60</span>
                  <input
                    id="mobile-local"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    value={formatLocalDigits(mobileLocalDigits)}
                    onChange={(e) => patchDraft({ mobileLocalDigits: parseLocalMobileInput(e.target.value) })}
                    className={mobileLocalInputClass}
                  />
                </div>
              </div>
            </SurveyContentPanel>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-10">
            <div className="flex flex-col items-center gap-3">
              {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
              <SurveyButton disabled={isSubmitting} onClick={openConfirm}>
                {t.phone.requestOtpButton}
              </SurveyButton>
            </div>
          </SurveyContentPanelInset>
        </div>
      </div>

      {showConfirmModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-sm rounded-md bg-white p-6 shadow-xl">
            <p className="m-0 text-center text-lg font-semibold text-black">{t.phone.confirmTitle}</p>
            <p className="mt-4 text-center text-base text-slate-700">
              {t.phone.confirmMobileLabel}
              <span className="font-semibold tabular-nums">{displayFullMalaysiaMobile(mobileLocalDigits)}</span>
            </p>
            <p className="mt-3 text-center text-sm text-slate-600">{t.phone.confirmPrompt}</p>
            {isVerifyingSecurity ? (
              <p className="mt-3 text-center text-sm text-slate-500 animate-pulse">
                {t.phone.verifyingSecurity}
              </p>
            ) : null}
            <div className="mt-6 flex justify-evenly gap-3">
              <SurveyButton disabled={isSubmitting} onClick={() => setShowConfirmModal(false)}>
                {t.phone.back}
              </SurveyButton>
              <SurveyButton disabled={isSubmitting} onClick={() => void handleConfirmRequestOtp()}>
                {t.phone.confirm}
              </SurveyButton>
            </div>
          </div>
        </div>
      ) : null}
    </SurveyBackgroundLayout>
  );
}
